import React, { useEffect, useState } from "react";
import axios from "../../axiosConfig";
import { ASSIGNABLE_PRODUCTS } from "./rfpUtils";
import { applyProductDecision, previewProductDecision } from "./scopeDiscoveryApi";
import { decisionItemReason, decisionItemTitle } from "./productDecision";

const API = "/api";

const DISPOSITION_LABEL = {
  will_update: "Will update",
  conflict: "Conflict — left unchanged",
  same_decision: "Same decision — left unchanged",
  has_product: "Already has a product",
  grid_edited: "Edited in the grid",
  already_set: "Already set by this decision",
};

function rowResult(row, pick, reopen) {
  if (row.disposition !== "will_update") return DISPOSITION_LABEL[row.disposition] || row.disposition;
  if (!pick?.included) return reopen ? "Stays decided" : "Stays open for review";
  return reopen ? "Will return to Gen Req" : `Will set ${pick.technology}`;
}

export default function RfpProductDecisionDialog({ jobId, item, initial, onClose, onApplied }) {
  const [choice, setChoice] = useState(initial?.choice || "");
  const [technology, setTechnology] = useState(initial?.technology || "Alteon");
  const reopen = Boolean(initial?.reopen);
  const [preview, setPreview] = useState(null);
  const [picks, setPicks] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!choice && !reopen) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setPreview(null);
    previewProductDecision(
      jobId,
      { item_id: item.item_id, choice: choice || null, technology: choice === "product" ? technology : null, reopen },
      { http: axios, apiBase: API }
    )
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        const fallback = reopen ? "Gen Req" : data?.technology || "Alteon";
        const next = {};
        for (const row of data?.rows || []) {
          if (row.disposition === "will_update") next[row.id] = { included: true, technology: fallback };
        }
        setPicks(next);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || "Could not preview the rows.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, item.item_id, choice, technology, reopen]);

  const eligibleRows = (preview?.rows || []).filter((row) => row.disposition === "will_update");
  const selectedRows = eligibleRows.filter((row) => picks[row.id]?.included);
  const updateCount = selectedRows.length;
  const skippedCount = eligibleRows.length - updateCount;
  const allSelected = eligibleRows.length > 0 && selectedRows.length === eligibleRows.length;
  const sharedTechnology = selectedRows.length && selectedRows.every((row) => picks[row.id]?.technology === picks[selectedRows[0].id]?.technology)
    ? picks[selectedRows[0].id].technology
    : "";
  const confirmLabel = !eligibleRows.length
    ? "Mark reviewed"
    : reopen
      ? `Return ${updateCount} row${updateCount === 1 ? "" : "s"} to Gen Req`
      : `Apply to ${updateCount} row${updateCount === 1 ? "" : "s"}`;
  const setPick = (rowId, patch) => {
    setPicks((current) => ({ ...current, [rowId]: { ...current[rowId], ...patch } }));
  };
  const setSelectedTechnology = (nextTechnology) => {
    setPicks((current) => {
      const next = { ...current };
      for (const row of eligibleRows) {
        if (next[row.id]?.included) next[row.id] = { ...next[row.id], technology: nextTechnology };
      }
      return next;
    });
  };

  const confirm = async () => {
    setApplying(true);
    setError("");
    try {
      const body = {
        item_id: item.item_id,
        choice: choice || null,
        technology: choice === "product" ? technology : null,
        reopen,
      };
      if (eligibleRows.length) {
        body.rows = selectedRows.map((row) => {
          const tech = picks[row.id]?.technology || "Gen Req";
          return {
            row_id: row.id,
            choice: tech === "Gen Req" ? "gen_req" : "product",
            technology: tech === "Gen Req" ? null : tech,
          };
        });
      }
      const data = await applyProductDecision(jobId, body, { http: axios, apiBase: API });
      onApplied?.(data);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save the product decision.");
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4" role="dialog" aria-modal="true" data-testid="rfp-product-decision-dialog">
      <div className="mt-8 w-full max-w-5xl rounded-xl border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{decisionItemTitle(item)}</h3>
            {decisionItemReason(item) ? (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{decisionItemReason(item)}</p>
            ) : null}
          </div>
          <button type="button" className="text-sm text-slate-500" onClick={onClose}>
            Close
          </button>
        </div>

        {!choice && !reopen ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Choose a starting product. In the preview you can include, skip, or assign a different product to each row. Skipped rows stay open for review.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-950" onClick={() => setChoice("gen_req")}>
                Keep as Gen Req
              </button>
              <select className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-950" value={technology} onChange={(event) => setTechnology(event.target.value)} aria-label="Product">
                {ASSIGNABLE_PRODUCTS.map((product) => (
                  <option key={product} value={product}>{product}</option>
                ))}
              </select>
              <button type="button" className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-medium text-white" onClick={() => setChoice("product")}>
                Assign product
              </button>
            </div>
          </div>
        ) : null}

        {loading ? <p className="mt-4 text-sm text-slate-500">Checking which rows would change…</p> : null}
        {error ? <p className="mt-4 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}

        {preview ? (
          <div className="mt-4">
            {preview.conflict_count ? (
              <p className="mb-2 text-sm text-amber-800 dark:text-amber-200">
                {preview.conflict_count} row{preview.conflict_count === 1 ? "" : "s"} already {preview.conflict_count === 1 ? "has" : "have"} a decision from another discovery item. Those rows stay as they are.
              </p>
            ) : null}
            {eligibleRows.length ? (
              <div className="mb-2 flex flex-wrap items-center gap-3 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(event) => {
                      const included = event.target.checked;
                      setPicks((current) => {
                        const next = { ...current };
                        for (const row of eligibleRows) next[row.id] = { ...next[row.id], included };
                        return next;
                      });
                    }}
                  />
                  Select all eligible rows
                </label>
                {reopen ? null : (
                  <label className="inline-flex items-center gap-2">
                    Set selected rows to
                    <select
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
                      aria-label="Set selected rows to"
                      value={sharedTechnology}
                      onChange={(event) => setSelectedTechnology(event.target.value)}
                    >
                      <option value="" disabled>Mixed</option>
                      <option value="Gen Req">Gen Req</option>
                      {ASSIGNABLE_PRODUCTS.map((product) => (
                        <option key={product} value={product}>{product}</option>
                      ))}
                    </select>
                  </label>
                )}
                <span className="text-slate-500">
                  {updateCount} will update
                  {skippedCount ? `. ${skippedCount} ${reopen ? "stay decided" : "stay open for review"}` : ""}.
                </span>
              </div>
            ) : (
              <p className="mb-2 text-sm text-slate-600 dark:text-slate-300">No eligible rows remain. Marking this item reviewed does not change any requirement.</p>
            )}
            <div className="max-h-80 overflow-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800">
                  <tr>
                    <th className="px-2 py-2">Include</th>
                    <th className="px-2 py-2">Row</th>
                    <th className="px-2 py-2">Product</th>
                    <th className="px-2 py-2">Requirement</th>
                    <th className="px-2 py-2">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => {
                    const pick = picks[row.id];
                    const editable = row.disposition === "will_update";
                    return (
                      <tr key={row.id} className="border-t border-slate-100 align-top dark:border-slate-800" data-testid={`rfp-decision-preview-${row.id}`}>
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            aria-label={`Include row ${row.sequence ?? row.id}`}
                            data-testid={`rfp-decision-include-${row.id}`}
                            disabled={!editable}
                            checked={Boolean(editable && pick?.included)}
                            onChange={(event) => setPick(row.id, { included: event.target.checked })}
                          />
                        </td>
                        <td className="px-2 py-2 text-slate-500">{row.sequence ?? row.id}</td>
                        <td className="px-2 py-2">
                          {editable && !reopen ? (
                            <select
                              className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm disabled:opacity-50 dark:border-slate-600 dark:bg-slate-950"
                              aria-label={`Product for row ${row.sequence ?? row.id}`}
                              data-testid={`rfp-decision-product-${row.id}`}
                              disabled={!pick?.included}
                              value={pick?.technology || "Gen Req"}
                              onChange={(event) => setPick(row.id, { technology: event.target.value })}
                            >
                              <option value="Gen Req">Gen Req</option>
                              {ASSIGNABLE_PRODUCTS.map((product) => (
                                <option key={product} value={product}>{product}</option>
                              ))}
                            </select>
                          ) : (
                            row.technology
                          )}
                        </td>
                        <td className="px-2 py-2 text-slate-700 dark:text-slate-200">{row.text}</td>
                        <td className="px-2 py-2">
                          <span className="font-medium">{rowResult(row, pick, reopen)}</span>
                          <span className="mt-0.5 block text-xs text-slate-500">{row.reason}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {!preview.rows.length ? (
                    <tr>
                      <td colSpan={5} className="px-2 py-3 text-slate-500">No requirement rows fall inside this discovery item.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                disabled={applying || (eligibleRows.length > 0 && updateCount === 0)}
                className="rounded-lg bg-amber-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
                onClick={confirm}
                data-testid="rfp-product-decision-confirm"
              >
                {applying ? "Saving…" : confirmLabel}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
